import {tokens, colors} from '@commercial-helios/ui/theme'
import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import Footer from '@/components/footer'
import Header from '@/components/header'
import SEO from '@/components/seo'
import {TypeformSurvey} from '@/components/typeform-survey'
import type {FaqPageQuery} from '@/graphql-types'
import upArrow from '@/images/icons/back-to-top-arrow.svg'
import QuoteCarousel from '@/sections/quote-carousel'
import SignupBannerSection from '@/sections/signup-banner'
import TitleWithAccordion from '@/sections/title-with-accordion'
import TitleWithIconGridSection from '@/sections/title-with-icon-grid'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import RichText, {hasRichText} from '@helios/shared/components/rich-text'
import {Main} from '@helios/shared/components/skip-to-content'
import SmoothScrollLink from '@helios/shared/components/smooth-scroll-link'
import {Section} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'
import getEnvironmentFormId from '@helios/shared/utils/get-environment-field'
import mq from '@helios/shared/utils/media-query'

type Props = PageProps<FaqPageQuery>

const FaqPage = ({data}: Props) => {
    const content = data.prismicFaqPage.data

    if (!content) {
        return handleNoPrismicData()
    }

    return (
        <>
            <Header />

            <TypeformSurvey />

            <Main>
                {hasRichText(content.title) && (
                    <TitleSection>
                        <RichText content={content.title} variant="heading-large" />
                        <RichText content={content.subtitle} variant="title-base" />
                    </TitleSection>
                )}

                <Section>
                    <FaqButtonsLayout>
                        <FaqCategory as={SmoothScrollLink} selector="#basic">
                            <Text>{content?.faq_basic_button_label}</Text>
                        </FaqCategory>
                        <FaqCategory as={SmoothScrollLink} selector="#use">
                            <Text>{content?.faq_use_button_label}</Text>
                        </FaqCategory>
                        <FaqCategory as={SmoothScrollLink} selector="#feature">
                            <Text>{content?.faq_feature_button_label}</Text>
                        </FaqCategory>
                        <FaqCategory as={SmoothScrollLink} selector="#security">
                            <Text>{content?.faq_security_button_label}</Text>
                        </FaqCategory>
                    </FaqButtonsLayout>
                </Section>

                {!!content.faq_basic?.length && (
                    <TitleWithAccordion
                        id="basic"
                        title={content.faq_basic_title}
                        collapsible
                        items={content.faq_basic?.map((item) => ({
                            title: item.question,
                            body: item.answer?.richText,
                        }))}
                    />
                )}

                {!!content.faq_use?.length && (
                    <TitleWithAccordion
                        id="use"
                        title={content.faq_use_title}
                        collapsible
                        items={content.faq_use.map((item) => ({
                            title: item.question,
                            body: item.answer?.richText,
                        }))}
                    />
                )}

                {!!content.faq_feature?.length && (
                    <TitleWithAccordion
                        id="feature"
                        title={content.faq_feature_title}
                        collapsible
                        items={content.faq_feature.map((item) => ({
                            title: item.question,
                            body: item.answer?.richText,
                        }))}
                    />
                )}

                {!!content.faq_security?.length && (
                    <TitleWithAccordion
                        id="security"
                        title={content.faq_security_title}
                        collapsible
                        items={content.faq_security.map((item) => ({
                            title: item.question,
                            body: item.answer?.richText,
                        }))}
                    />
                )}

                {content.testimonials && (
                    <QuoteCarousel
                        items={content.testimonials.map(({testimonial}) => ({
                            image: testimonial.document.data.image,
                            quote: testimonial.document.data.quote,
                            attribution: {
                                name: testimonial.document.data.attribution_name,
                                title: testimonial.document.data.attribution_title,
                            },
                        }))}
                    />
                )}

                {content.social_proof_logos_extended_section && (
                    <TitleWithIconGridSection
                        titleVariant="heading-large"
                        highlightColor="yellow"
                        title={
                            content.social_proof_logos_extended_section.document.data.title.richText
                        }
                        items={content.social_proof_logos_extended_section.document.data.logos.map(
                            ({logo}) => ({
                                icon: logo,
                            })
                        )}
                    />
                )}

                {content.social_proof_ratings_section && (
                    <TitleWithIconGridSection
                        titleVariant="heading-large"
                        highlightColor="pink"
                        title={content.social_proof_ratings_section.document.data.title.richText}
                        items={content.social_proof_ratings_section.document.data.ratings.map(
                            ({rating}) => ({
                                icon: rating.document.data.logo,
                                stars: rating.document.data.rating_number,
                                link: rating.document.data.link,
                            })
                        )}
                    />
                )}

                {content.signup_banner && (
                    <SignupBannerSection
                        {...content.signup_banner.document.data}
                        formId={getEnvironmentFormId(
                            content.signup_banner.document.data.hubspot_form_id_production,
                            content.signup_banner.document.data.hubspot_form_id_staging
                        )}
                        placeholder={content.signup_banner.document.data.email_input_placeholder}
                        submitText={content.signup_banner.document.data.submit_text}
                        errorText={content.signup_banner.document.data.email_input_error_text}
                        color="pink"
                    />
                )}
            </Main>

            <Footer />
        </>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<FaqPageQuery>) => {
    const pageData = data.prismicFaqPage.data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
        />
    )
}

const TitleSection = styled(Section)`
    display: grid;
    row-gap: ${tokens.spacing20};

    ${mq.min('sm')} {
        text-align: center;
    }
`

const FaqButtonsLayout = styled.div`
    display: grid;
    gap: ${tokens.spacing40};

    ${mq.min('sm')} {
        row-gap: ${tokens.spacing32};
        column-gap: 72px;
        grid-template-columns: repeat(2, 1fr);
    }

    ${mq.min('md')} {
        column-gap: ${tokens.spacing40};
        grid-template-columns: repeat(4, 1fr);
    }
`

const FaqCategory = styled.a`
    display: flex;
    padding: ${tokens.spacing20};
    line-height: 0;
    background: ${colors.background.muted};
    border: 1px solid transparent;
    border-radius: ${tokens.arc8};
    justify-content: space-between;
    align-items: center;
    row-gap: ${tokens.arc8};

    ::after {
        content: url(${upArrow});
        transform: rotate(180deg);
    }

    :hover,
    :focus,
    :active {
        cursor: pointer;
        border-color: ${colors.border.secondary};
    }
`

export const query = graphql`
    query FaqPage($locale: String!) {
        prismicFaqPage(lang: {eq: $locale}) {
            _previewable
            data {
                title
                subtitle
                faq_basic_title
                faq_basic_button_label
                faq_use_title
                faq_use_button_label
                faq_feature_title
                faq_feature_button_label
                faq_security_title
                faq_security_button_label
                meta_title
                meta_description
                open_graph_image {
                    alt
                    copyright
                    url
                    dimensions {
                        height
                        width
                    }
                }
                faq_security {
                    question
                    answer {
                        richText
                    }
                }
                faq_feature {
                    question
                    answer {
                        richText
                    }
                }
                faq_use {
                    question
                    answer {
                        richText
                    }
                }
                faq_basic {
                    question
                    answer {
                        richText
                    }
                }
                testimonials {
                    testimonial {
                        document {
                            ... on PrismicTestimonial {
                                id
                                data {
                                    image {
                                        alt
                                        copyright
                                        url
                                        dimensions {
                                            height
                                            width
                                        }
                                    }
                                    quote
                                    attribution_name
                                    attribution_title
                                    cta_text
                                }
                            }
                        }
                    }
                }
                social_proof_logos_extended_section {
                    document {
                        ... on PrismicSocialProofLogosExtendedSection {
                            data {
                                title {
                                    richText
                                }
                                logos {
                                    logo {
                                        alt
                                        copyright
                                        url
                                        dimensions {
                                            height
                                            width
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                social_proof_ratings_section {
                    document {
                        ... on PrismicSocialProofRatingsSection {
                            data {
                                title {
                                    richText
                                }
                                ratings {
                                    rating {
                                        document {
                                            ... on PrismicRating {
                                                data {
                                                    logo {
                                                        alt
                                                        copyright
                                                        url
                                                        dimensions {
                                                            height
                                                            width
                                                        }
                                                    }
                                                    stars
                                                    rating_number
                                                    link
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                signup_banner {
                    document {
                        ...SignupBanner
                    }
                }
            }
        }
    }
`

export default withPrismicPreview(FaqPage)
